package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonClassDiscriminator
import kotlin.jvm.JvmInline
import kotlin.time.Instant

/**
 * Strategie-Modell (Spec §3.1): typisierte, polymorphe DTO-Hierarchien für Params,
 * Options und Status. Diskriminator ist `strategyId` (per [JsonClassDiscriminator]),
 * auf jvm und js identisch — per commonTest abgesichert.
 */

@Serializable
@JvmInline
value class StrategyId(val value: String) {
    override fun toString(): String = value
}

@Serializable
enum class StrategyPhase { INACTIVE, ACTIVATING, ACTIVE, DEACTIVATING, ERROR }

/** Maschinenlesbare Klassifikation eines Feldfehlers aus `validate()` (§3.2/§3.4). */
@Serializable
enum class ValidationCode {
    /** strukturelle Invariante verletzt */
    INVALID,

    /** Options-Mitgliedschaft verletzt — Auswahl veraltet, `describe` neu laden (§3.4) */
    STALE_OPTION,

    /** unbekannte `schemaVersion` — Controller-App aktualisieren (§3.1) */
    UNSUPPORTED_SCHEMA,
}

@Serializable
data class ValidationError(
    val field: String,
    val message: String,
    val code: ValidationCode = ValidationCode.INVALID,
    /** Zweisprachige Fassung von [message]; null bei alten Gegenstellen. */
    val messageText: RemoteText? = null,
)

/**
 * Zweisprachiger Anzeigetext auf dem Draht: der Desktop rendert BEIDE Sprachen an der
 * Relay-Grenze (Muster wie LocalizedText der Release-Notes), der Controller wählt lokal.
 * Immer additiv NEBEN dem bestehenden deutschen String-Feld — alte Gegenstellen
 * ignorieren das Feld (`ignoreUnknownKeys`) bzw. lesen weiter das alte Feld; darum
 * keine schemaVersion-Erhöhung.
 */
@Serializable
data class RemoteText(val de: String, val en: String)

/**
 * Strategie-Eingabeparameter, polymorph über `strategyId`. Schema-Evolution über
 * [schemaVersion] pro Params-Typ (§3.1): additive Evolution bevorzugen; Breaking
 * Changes erhöhen die Version, der Desktop lehnt unbekannte Versionen mit `BAD_ARGS` ab.
 */
@Serializable
@JsonClassDiscriminator("strategyId")
sealed interface StrategyParams {
    val schemaVersion: Int
}

/** Dynamische Auswahloptionen vom Desktop (§3.4); [asOf] dient Anzeige/Diagnose. */
@Serializable
@JsonClassDiscriminator("strategyId")
sealed interface StrategyOptions {
    val asOf: Instant
}

/**
 * Remote-Status-Projektion einer Strategie. [activeParams] zeigt, was tatsächlich
 * läuft — für zweite Controller und die Reconciliation nach UNKNOWN (§3.3).
 */
@Serializable
@JsonClassDiscriminator("strategyId")
sealed interface RemoteStrategyStatus {
    val strategyId: StrategyId
    val phase: StrategyPhase
    val activeParams: StrategyParams?
    val since: Instant?
    val message: String?
    /** Zweisprachige Fassung von [message]; null bei alten Desktops. */
    val messageText: RemoteText?
}

/** Benannte Voreinstellung; [name] ist sanitisiert anzuzeigen (Protokoll-§7.6). */
@Serializable
data class Preset<P : StrategyParams>(val name: String, val params: P)

/** Ergebnis von `strategy.describe` (§3.1): Prefill + Presets + aktuelle Options. */
@Serializable
data class StrategyDescription<P : StrategyParams, O : StrategyOptions>(
    /** Voreinstellung/Prefill (z. B. zuletzt verwendet), null = keine */
    val defaults: P? = null,
    val presets: List<Preset<P>> = emptyList(),
    /** aktuelle Auswahloptionen vom Desktop */
    val options: O,
)

@Serializable
data class StrategySummary(
    val id: StrategyId,
    val displayName: String,
    val phase: StrategyPhase,
    val activeParams: StrategyParams? = null,
)

/** Ergebnis eines idempotenten Zustandsübergangs (§3.3). */
@Serializable
sealed interface TransitionResult {
    @Serializable
    @SerialName("applied")
    data class Applied(val status: RemoteStrategyStatus) : TransitionResult

    /** z. B. `activate` bei ACTIVE — der Status enthält [RemoteStrategyStatus.activeParams] zum Vergleich. */
    @Serializable
    @SerialName("alreadyInTargetState")
    data class AlreadyInTargetState(val status: RemoteStrategyStatus) : TransitionResult

    @Serializable
    @SerialName("rejected")
    data class Rejected(
        val reason: String,
        /** Zweisprachige Fassung von [reason]; null bei alten Desktops. */
        val reasonText: RemoteText? = null,
    ) : TransitionResult
}

/**
 * Host-konfigurierbare Grenzen pro Strategie (§4/§5): die einzige inhaltliche
 * Laufzeitschranke, da es kein Confirmation-Gate gibt (§2.1). Struktur & Auswahl
 * gehören in `validate()` (Registry), Limits gehören dem Betreiber (Policy).
 */
interface ParamsBounds<in P : StrategyParams> {
    /** leer = innerhalb der Grenzen; sonst Betreiber-lesbare Verstöße (⇒ `POLICY_DENIED`-msg). */
    fun violations(params: P): List<String>

    /**
     * Zweisprachige Fassung von [violations]. Default spiegelt die deutschen Texte in
     * beide Sprachen; Bounds-Implementierungen überschreiben mit echter EN-Übersetzung
     * und leiten [violations] daraus ab (eine Logik, zwei Sprachen).
     */
    fun violationsText(params: P): List<RemoteText> = violations(params).map { RemoteText(it, it) }
}

// ---- Kommando-Args/-Results (Draht) -----------------------------------------------

@Serializable
data class StrategyIdArgs(val strategyId: StrategyId)

@Serializable
data class ActivateArgs(val strategyId: StrategyId, val params: StrategyParams)

@Serializable
data class StrategyListResult(val strategies: List<StrategySummary>)

@Serializable
data class StrategyStatusResult(val status: RemoteStrategyStatus)

/** Payload des `strategy.update`-Push-Events (§2). */
@Serializable
data class StrategyUpdate(val strategyId: StrategyId, val status: RemoteStrategyStatus)

/**
 * Preisquelle der offenen Positionen eines Trades (Review §11 Nr. 7, §16 F12): sagt dem Client,
 * wie belastbar [TradePnlWire.unrealizedPnl] und [TradePnlWire.dailyPnl] sind. Der Desktop
 * verwirft Preise minderer Qualität NICHT, er markiert sie.
 */
@Serializable
enum class PriceSourceWire {
    /** Echtzeit-Marktpreis (Mark bzw. Bid/Ask-Mid, Aktien/Futures Last in RTH). */
    LIVE,

    /** Verzögerter Marktpreis — Betrag nur annähernd aktuell. */
    DELAYED,

    /** Letzter Schlusskurs: Markt geschlossen oder kein Live-Feed — Stand des letzten Handelstags. */
    CLOSED,

    /** Keine offene Position mit Preis: Trade vollständig realisiert oder kein Preis verfügbar (nie 0 als Ersatz). */
    NONE,
}

/**
 * P&L-Zeile je Strategie in [DesktopStatus.strategyPnl] (Review §14.3, N1–N3): Aggregat über die
 * Trades aus [DesktopStatus.tradePnl], die dieser Strategie zugeordnet sind (Auswahlregel N2 dort).
 * Alle Beträge in [DesktopStatus.currency], vom Desktop auf 2 Nachkommastellen gerundet;
 * null = nicht berechenbar (fehlender Vortagesschluss, fehlender FX-Kurs) — nie 0 als Ersatz.
 * Fehlt der Wert nur für einzelne Trades, trägt die Zeile die Teilsumme der berechenbaren Trades
 * (Review §16 F4); [dailyAvailable] markiert die Lücke.
 */
@Serializable
data class StrategyPnlWire(
    /**
     * `StrategyDB.ident` der Strategie; null = Gruppe „ohne Strategie“ (Trades ohne Zuordnung),
     * dann ist [name] der vom Desktop gelieferte Anzeigetext der Gruppe.
     */
    val ident: String?,
    /** Anzeigename aus Desktop-Quelle — vor Anzeige sanitisieren (Protokoll-§7.6, wie [DesktopStatus.accountNames]). */
    val name: String,
    /** Tages-P&L (N1): Σ [TradePnlWire.dailyPnl] der berechenbaren Trades; null, wenn kein Trade einen Wert liefert. */
    val dailyPnl: Decimal? = null,
    /** Σ [TradePnlWire.realizedPnl] der berechenbaren Trades; null, wenn kein Trade einen Wert liefert. */
    val realizedPnl: Decimal? = null,
    /** Σ [TradePnlWire.unrealizedPnl] der berechenbaren Trades; null, wenn kein Trade einen Wert liefert. */
    val unrealizedPnl: Decimal? = null,
    /** Σ [TradePnlWire.netPnl] (realisiert + unrealisiert − Kommission) der berechenbaren Trades; null wie oben. */
    val netPnl: Decimal? = null,
    /** Anzahl der Trades dieser Strategie in [DesktopStatus.tradePnl]. */
    val tradeCount: Int,
    /** true, wenn [dailyPnl] für ALLE Trades der Strategie berechenbar war; false ⇒ Teilsumme oder null. */
    val dailyAvailable: Boolean,
)

/**
 * Offene Position eines Trades je Symbol in [TradePnlWire.positions] (Review §17 F19). Mengen sind
 * vorzeichenbehaftet (Short negativ), Preise bleiben in der ORIGINALWÄHRUNG des Kontrakts und
 * unverändert in der Genauigkeit des Desktops — anders als die P&L-Beträge der Trade-Zeile werden
 * sie nicht in [DesktopStatus.currency] umgerechnet und nicht auf 2 Nachkommastellen gerundet;
 * Clients rechnen daraus keine P&L nach, sie zeigen an. null = Wert am Desktop nicht bekannt
 * (z. B. kein Preis ⇒ [marketPrice]/[priceAt] null und [priceSource] = [PriceSourceWire.NONE]).
 */
@Serializable
data class PositionWire(
    /** Symbol in LOT-Notation (identisch zum Eintrag in [TradePnlWire.symbols]). */
    val symbol: String,
    /** Positionsgröße in Kontrakten/Stück, vorzeichenbehaftet: Long positiv, Short negativ. Nie 0 für offene Positionen. */
    val quantity: Decimal,
    /** Kontrakt-Multiplikator (Optionen 100, Futures laut Kontrakt, Aktien 1); null = am Desktop nicht bekannt. */
    val multiplier: Decimal? = null,
    /** Durchschnittlicher Einstiegspreis der offenen Lots (je Einheit, Originalwährung); null = nicht berechenbar. */
    val avgEntryPrice: Decimal? = null,
    /** Letzter Preis laut [priceSource] (je Einheit, Originalwährung); null ⇔ [priceSource] = [PriceSourceWire.NONE]. */
    val marketPrice: Decimal? = null,
    /** Zeitpunkt von [marketPrice] als Unix-Epoche in SEKUNDEN (UTC); null ohne Preis. Bewusst Long statt Instant — kleinster gemeinsamer Nenner für SPA/iOS/Watch. */
    val priceAt: Long? = null,
    /** Preisquelle DIESER Position; [TradePnlWire.priceSource] ist die schwächste Quelle über alle Positionen des Trades. */
    val priceSource: PriceSourceWire,
)

/**
 * P&L-Zeile je Trade in [DesktopStatus.tradePnl] (Review §14.3, N1–N3). Beträge in
 * [DesktopStatus.currency], vom Desktop auf 2 Nachkommastellen gerundet — bewusst KEINE
 * Originalwährungs-Beträge (N3). null = nicht berechenbar (Vortagesschluss/FX fehlt,
 * Review §16 F4/F5) — nie 0 als Ersatz. Ohne Preis für eine offene Position ist [unrealizedPnl]
 * null und [priceSource] = [PriceSourceWire.NONE].
 *
 * F19 (additiv, Defaults): [positions] trägt die offenen Positionen mit vorzeichenbehafteter Menge
 * und Preisdetails; [symbols] bleibt als Ableitung daraus für Clients, die nur Symbole zeigen.
 * [settlement] weist das in [realizedPnl] enthaltene Cash-Settlement separat aus, [priorCloseDay]
 * nennt den Vortagesschluss, auf den sich [dailyPnl] bezieht.
 */
@Serializable
data class TradePnlWire(
    /** Trade-UUID des Desktops — stabiler Schlüssel für Diffs/Animationen im Client. */
    val tradeId: String,
    /** Trade-Name aus Desktop-Quelle — vor Anzeige sanitisieren (Protokoll-§7.6). */
    val name: String,
    /** `StrategyDB.ident`; null = ohne Strategie (Gruppe [StrategyPnlWire] mit `ident == null`). */
    val strategyIdent: String?,
    /** Anzeigename der Strategie bzw. der Gruppe „ohne Strategie“, identisch zu [StrategyPnlWire.name]; sanitisieren. */
    val strategyName: String,
    /**
     * Konto als Anzeigetext aus Desktop-Quelle (Kontoname der Desktop-Kontenverwaltung, sonst
     * Kontonummer) — vor Anzeige sanitisieren (Protokoll-§7.6). Kein Schlüssel in die Konto-Maps.
     */
    val account: String,
    /**
     * Offene Positionen des Trades je Symbol mit vorzeichenbehafteter Menge (F19), Reihenfolge wie
     * am Desktop. Leer = keine offene Position (vollständig realisiert), älterer Desktop ohne das
     * Feld — oder Streaming-Stufe 1 ([DesktopStatus.netLiquidationMasked]): Menge × Preis verriete
     * das Exposure, darum sendet der Desktop dann KEINE Positionen; [symbols] bleibt gefüllt und
     * [priceSource] gilt weiter. In allen drei Fällen ist [symbols] die einzige Symbolquelle.
     */
    val positions: List<PositionWire> = emptyList(),
    /**
     * Symbole der Positionen des Trades in LOT-Notation, Reihenfolge wie am Desktop. Default ist die
     * Ableitung aus [positions]; bleibt als eigenes Feld für Clients, die nur Symbole zeigen, für
     * ältere Desktops, die noch keine [positions] senden, und für Streaming-Stufe 1, in der der
     * Desktop [positions] bewusst leer lässt (Symbole bleiben sichtbar, Mengen nicht).
     */
    val symbols: List<String> = positions.map { it.symbol },
    /**
     * Realisierter Kursgewinn aller geschlossenen Lots des Trades INKLUSIVE Cash-[settlement],
     * ohne Kommission (so liefert `PnlFigures.realized` des Desktops).
     */
    val realizedPnl: Decimal? = null,
    /**
     * Cash-Settlement des Trades (Barausgleich bei Verfall/Ausübung, Vorzeichen wie P&L); bereits
     * Bestandteil von [realizedPnl], hier nur separat ausgewiesen — nicht erneut addieren.
     * null = kein Settlement oder älterer Desktop ohne das Feld.
     */
    val settlement: Decimal? = null,
    /** Unrealisierte P&L der offenen Lots zum Preis laut [priceSource] (ohne Kommission). */
    val unrealizedPnl: Decimal? = null,
    /**
     * Tages-P&L (N1): Net(jetzt) − Net(gestriger New-York-Schluss) aus den Lots des Trades,
     * heute eröffnete Lots gegen Entry, Kommission/Settlement des Tages inklusive.
     * null ⇔ [dailyAvailable] = false.
     */
    val dailyPnl: Decimal? = null,
    /** Kommission aller Executions des Trades (Entry- und Exit-Anteile), positiv = Kosten. */
    val commission: Decimal? = null,
    /** Netto: [realizedPnl] + [unrealizedPnl] − [commission]; null, sobald ein Summand nicht berechenbar ist. */
    val netPnl: Decimal? = null,
    /** Preisquelle der offenen Positionen; [PriceSourceWire.NONE] ohne offene Position mit Preis. */
    val priceSource: PriceSourceWire,
    /** false = Tages-P&L nicht berechenbar (kein Vortagesschluss oder älter als 4 Kalendertage, F5) ⇒ [dailyPnl] null. */
    val dailyAvailable: Boolean,
    /**
     * ISO-Datum (`YYYY-MM-DD`, New-York-Kalendertag) des Vortagesschlusses, gegen den [dailyPnl]
     * gerechnet wurde — in der Regel der letzte Handelstag vor [DesktopStatus.tradingDay], bei
     * Lücken ein älterer (F5: höchstens 5 Kalendertage). Gesetzt nur, wenn [dailyAvailable];
     * null sonst oder bei älteren Desktops ohne das Feld.
     */
    val priorCloseDay: String? = null,
    /**
     * Trade-Zustand des Desktops als String (`PLANNED`/`ACTIVE`/`ARCHIVED`, business `TradeState`);
     * absichtlich kein Enum, damit neue Zustände additiv bleiben. Nach N2 kommen nur `ACTIVE` und
     * heute geschlossene Trades auf den Draht.
     */
    val state: String,
)

/**
 * Desktop-Zustand für `desktop.status`/`desktop.update`: die TWS-Verbindung
 * ([com.xorrus.trading.lot.app.connection]-`connectedState`, also Betriebsbereitschaft,
 * nicht nur Socket) plus Live-Kontozahlen aus der TWS-Subscription. Additiv erweiterbar
 * (`ignoreUnknownKeys`) — alle Felder brauchen Defaults, damit ältere Gegenstellen
 * in beide Richtungen kompatibel bleiben.
 */
@Serializable
data class DesktopStatus(
    val twsConnected: Boolean = false,
    /**
     * Systemwährung des Desktops (ISO-Code, `Configuration.systemCurrency`) — die Währung,
     * in der ALLE Beträge dieses Status denominieren; der Desktop rechnet vor dem Draht um.
     * Null = älterer Desktop ohne Kontozahlen.
     */
    val currency: String? = null,
    /**
     * Tages-PnL je Account, umgerechnet in [currency].
     * Leer = keine Daten: TWS getrennt, noch kein Update oder älterer Desktop.
     */
    val dailyPnlByAccount: Map<String, Decimal> = emptyMap(),
    /** Nettoliquidierungswert je Account, umgerechnet in [currency]; leer = keine Daten wie oben. */
    val netLiquidationByAccount: Map<String, Decimal> = emptyMap(),
    /**
     * Tages-PnL je Account UNumgerechnet in der Basiswährung des Accounts — zusätzlich
     * zu [dailyPnlByAccount], der Client entscheidet, was er anzeigt. Enthält auch
     * Einträge, die in der umgerechneten Map fehlen (Quellwährung/Kurs unbekannt).
     */
    val dailyPnlOriginalByAccount: Map<String, MonetaryAmount> = emptyMap(),
    /** Nettoliquidierungswert je Account unumgerechnet; Pendant zu [dailyPnlOriginalByAccount]. */
    val netLiquidationOriginalByAccount: Map<String, MonetaryAmount> = emptyMap(),
    /**
     * Anzeigename je Account (Kontonummer → Name aus der Desktop-Kontenverwaltung) für die
     * Schlüssel der Kennzahlen-Maps; fehlt ein Eintrag, zeigt der Client die Kontonummer.
     * Anzeigetext aus Desktop-Quelle — vor Anzeige sanitisieren (Protokoll-§7.6).
     */
    val accountNames: Map<String, String> = emptyMap(),
    /**
     * Streaming-Modus am Desktop: die absoluten Kontowerte (NLV) werden bewusst NICHT
     * gesendet (beide NLV-Maps leer) und sollen im Client maskiert (z. B. `********`)
     * dargestellt werden. Default false — ältere Desktops kennen das Feld nicht.
     */
    val netLiquidationMasked: Boolean = false,
    /**
     * Streaming-Modus Stufe 2 am Desktop: zusätzlich zu den NLV-Werten werden auch die
     * Tages-PnL-Werte bewusst NICHT gesendet (beide PnL-Maps leer) und sollen im Client
     * maskiert dargestellt werden. Impliziert [netLiquidationMasked]. Default false —
     * ältere Desktops kennen das Feld nicht.
     *
     * Gilt ebenso für [strategyPnl] und [tradePnl] (beide Listen leer, N2): Trade-/Strategie-P&L
     * ist P&L und fällt damit unter dieselbe Stufe. Bewusst KEIN eigenes `tradePnlMasked` — ein
     * zweites Flag könnte nur den widersprüchlichen Zustand „Trade-P&L maskiert, Konto-P&L nicht“
     * ausdrücken. Client-Regel: `dailyPnlMasked` ⇒ Platzhalter statt Zeilen; sonst leere Liste ⇒
     * „keine Trades heute“ bzw. älterer Desktop ohne das Feld („—“).
     */
    val dailyPnlMasked: Boolean = false,
    /**
     * P&L je Strategie (Review §14.3, N1–N4): alle Strategien mit mindestens einem Trade aus
     * [tradePnl], plus die Gruppe „ohne Strategie“ (`ident == null`), sofern sie Trades hat.
     * Beträge in [currency]. Leer = keine passenden Trades, Streaming-Stufe 2 ([dailyPnlMasked])
     * oder älterer Desktop ohne das Feld. Gleiches 1-s-Sampling wie die Kontozahlen.
     */
    val strategyPnl: List<StrategyPnlWire> = emptyList(),
    /**
     * P&L je Trade (Review §14.3, N1–N4). Auswahlregel N2: Trades im Zustand `ACTIVE` oder mit
     * mindestens einem heute geschlossenen Lot; „heute“ ist der New-York-Kalendertag.
     * Beträge in [currency]. Leer = wie bei [strategyPnl].
     *
     * Erwartungsmanagement (Review §16): Σ [TradePnlWire.dailyPnl] ≠ Σ [dailyPnlByAccount] ist
     * normal — Mark- vs. Schlusskurse, FX-Cash, Dividenden/Zinsen und nicht zugeordnete
     * Executions liegen nur in der Konto-P&L des Brokers.
     */
    val tradePnl: List<TradePnlWire> = emptyList(),
    /**
     * ISO-Datum (`YYYY-MM-DD`) des New-York-Handelstags, auf den sich die `dailyPnl`-Werte in
     * [tradePnl] und [strategyPnl] beziehen (F19): „heute“ der Auswahlregel N2. Vor Börsenöffnung
     * und nach Schluss ist das der Kalendertag in New York, nicht der des Clients. Null bei älteren
     * Desktops ohne das Feld. Die Konto-P&L [dailyPnlByAccount] folgt dem Tagesraster des Brokers.
     */
    val tradingDay: String? = null,
    /**
     * true = [tradePnl] und [strategyPnl] stammen aus dem LETZTEN erfolgreich berechneten Ergebnis
     * des Desktops; das Neuladen (Datenbank, Kurse) schlägt gerade fehl, der Desktop versucht es mit
     * Backoff weiter. Die Listen sind dann brauchbar, aber möglicherweise veraltet — Clients
     * markieren sie, verwerfen sie nicht. Default false: Ergebnis aktuell, Listen leer/maskiert
     * oder älterer Desktop ohne das Feld.
     */
    val pnlStale: Boolean = false,
    /**
     * true = die Beträge in [tradePnl] und [strategyPnl] sind unvollständig: mindestens eine offene
     * Position ohne Kurs (deren `unrealizedPnl`/`netPnl` sind null) oder ein Lot ohne Wechselkurs in
     * die Systemwährung (realisierte Beträge sind Teilsummen). Pendant zum Desktop-Marker
     * „unvollständig“ (Warnfarbe); Clients markieren, verwerfen nicht. Default false: vollständig,
     * Listen leer/maskiert oder älterer Desktop ohne das Feld.
     */
    val pnlIncomplete: Boolean = false,
    /**
     * Update-Channel der laufenden Desktop-Installation (`stable`/`latest`/`dev`) —
     * die connect-SPA leitet damit auf die passende Strategie-SPA-Variante weiter,
     * die Strategie-SPA prüft damit nach Resume, ob sie noch die richtige ist.
     * Null = älterer Desktop ohne Channel-Meldung (oder Dev-Lauf ohne Preloader).
     */
    val channel: String? = null,
    /** App-Version des Desktops (`PreConfiguration.version`); nur Diagnose/Logging. Null = älterer Desktop. */
    val version: String? = null,
    /**
     * Lizenz-Feature FEATURE_HIDE_DISCLAIMER am Desktop: true = der Controller darf den
     * Risikohinweis vor Order-Buttons ausblenden. Default false — ohne Lizenz-Feature
     * (und bei älteren Desktops ohne das Feld) wird der Hinweis angezeigt.
     */
    val hideDisclaimer: Boolean = false,
)

/**
 * Gemeinsame Json-Konfiguration beider Seiten: tolerant gegen additive Evolution
 * (`ignoreUnknownKeys`), `schemaVersion` immer auf dem Draht (`encodeDefaults`).
 */
val LotJson: Json = Json {
    ignoreUnknownKeys = true
    explicitNulls = false
    encodeDefaults = true
}

/**
 * Feldfehler-Transport in `BAD_ARGS.msg`: der Desktop kodiert die `validate()`-Fehler
 * als JSON, die Fassade dekodiert sie zurück (⇒ `StaleOptions`-Erkennung, §3.4/§6).
 */
/**
 * POLICY_DENIED-Transport analog [FieldErrors]: der Desktop kodiert den lesbaren Grund
 * plus zweisprachige Fassung als JSON in `msg`; die Fassade dekodiert zurück. Ältere
 * Fassaden zeigen das rohe msg-Feld — Channel-Lockstep von Desktop und SPA begrenzt
 * diesen Zustand auf den Rollout.
 */
@Serializable
data class PolicyDeniedPayload(val message: String, val messageText: RemoteText? = null)

object PolicyDeniedTransport {
    fun encode(payload: PolicyDeniedPayload): String = LotJson.encodeToString(payload)

    fun decodeOrNull(msg: String?): PolicyDeniedPayload? {
        if (msg.isNullOrBlank()) return null
        return try {
            LotJson.decodeFromString<PolicyDeniedPayload>(msg)
        } catch (_: Exception) {
            null
        }
    }
}

object FieldErrors {
    fun encode(errors: List<ValidationError>): String = LotJson.encodeToString(errors)

    fun decodeOrNull(msg: String?): List<ValidationError>? {
        if (msg.isNullOrBlank()) return null
        return try {
            LotJson.decodeFromString<List<ValidationError>>(msg)
        } catch (_: Exception) {
            null
        }
    }
}
